import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import useEmergingAI from "../utils/useEmergingAI";
import EmergingAICareers from "../components/recommendCareers/EmergingAICareers";
import Loader from "../components/loader/Loader";
import Sidebar from "../components/Sidebar";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";

const EmergingCareers = () => {
  const { currentJob } = useSelector((state) => state?.user);
  const userId = useSelector((state) => state.auth.id);
  const queryClient = useQueryClient();
  const [savedCareers, setSavedCareers] = useState([]);

  const {
    data: emergingAIData,
    isLoading: emergingIsPending,
    error: emergingError,
  } = useEmergingAI(currentJob);

  useEffect(() => {
    if (currentJob) {
      // Invalidate all queries that depend on currentJob
      queryClient.invalidateQueries({
        queryKey: ["emergingAIGraph"],
        exact: false
      });
    }
  }, [currentJob, queryClient]);

  useEffect(() => {
    fetchSavedCareers();
  }, [userId]);

  const fetchSavedCareers = async () => {
    if (!userId) {
      return;
    }
    
    try {
      const response = await api1.get(`${API_ENDPOINTS.GET_CAREER_BOOKMARKS}/${userId}`);
      const savedCareersData = response.data.data || [];
      setSavedCareers(savedCareersData);
    } catch (error) {
      console.error("Error fetching saved careers:", error);
      setSavedCareers([]);
    }
  };

  const isCareerSaved = (career) => {
    if (!career || !savedCareers.length) {
      return false;
    }
    
    const isSaved = savedCareers.some(saved => {
      // Match by career name and type
      const nameMatch = saved.careerName === career.name || saved.title === career.name;
      const typeMatch = saved.careerType === "emerging" || saved.category === "emerging";
      
      return nameMatch && typeMatch;
    });
    
    return isSaved;
  };

  const emergingCareers = useMemo(() => {
    if (!emergingAIData?.response) return [];
    const allCareers = [
      ...(emergingAIData.response.top_3_similar_ai_emerging_occupations || []),
      ...(emergingAIData.response.top_3_advance_ai_emerging_occupations || []),
      ...(emergingAIData.response.top_3_lateral_ai_emerging_occupations || []),
    ];
    return allCareers;
  }, [emergingAIData]);

  if (emergingIsPending) {
    return <Loader />
  }

  return (
    <Sidebar>
      <EmergingAICareers
        careers={emergingCareers}
        heading="Emerging AI Careers"
        subtitle="High-growth opportunities in the AI revolution"
        isLoading={emergingIsPending}
        isEmerging={true}
        userId={userId}
        isCareerSaved={isCareerSaved}
        onBookmarkChange={fetchSavedCareers}
      />
    </Sidebar>
  );
};

export default EmergingCareers;